import { create } from 'zustand';
import { trpc } from '../lib/trpc';

/**
 * Iso Sprite Store
 *
 * Manages generated full-body iso sprites for the HD-2D view.
 * Every entity (character or mob instance) is identified by its UUID —
 * "Fire Newt Warrior 1" and "Fire Newt Warrior 2" get their OWN 4-facing
 * sprite set so each instance looks unique on the board.
 *
 * localStorage schema v2 (id-keyed):
 *   "machina:iso-sprites"      — { "uuid:facing" → base64 }
 *   "machina:iso-sprite-seeds" — { "uuid" → base64 }
 *   "machina:iso-sprite-names" — { "uuid" → display name for UI lookup }
 *
 * v1 (name-keyed) entries are dropped on load — user regens once.
 */

const CACHE_KEY = 'machina:iso-sprites';
const SEED_CACHE_KEY = 'machina:iso-sprite-seeds';
const NAME_CACHE_KEY = 'machina:iso-sprite-names';
const SCHEMA_VERSION_KEY = 'machina:iso-sprites:schema';
const CURRENT_SCHEMA = 'v2';

// UUID-ish detector (covers standard UUIDs + our short-id style)
const UUID_LIKE = /^[0-9a-f-]{8,}$/i;

export type SpriteFacing = 'south' | 'north' | 'east' | 'west';
export const ALL_FACINGS: SpriteFacing[] = ['south', 'north', 'east', 'west'];

// Facings actually generated by the bulk-gen path. LLM output isn't consistent
// across directions yet (each facing is a separate FLUX call), so we ship one
// forward sprite and reuse it. Per-facing manual gen (MenageriePanel) still
// works on the full set — flip this back to ALL_FACINGS once consistency lands.
export const ACTIVE_FACINGS: SpriteFacing[] = ['south'];

/** Arrow key → facing direction mapping for isometric view */
export const ARROW_TO_FACING: Record<string, SpriteFacing> = {
  ArrowDown: 'south',
  ArrowUp: 'north',
  ArrowRight: 'east',
  ArrowLeft: 'west',
};

function spriteKey(id: string, facing: SpriteFacing): string {
  return `${id}:${facing}`;
}

/**
 * Persist whatever facings exist right now for this entity. Called after
 * each facing gen so partial progress survives a failure or refresh.
 * Reads fresh state at call time — don't cache the map.
 */
async function persistCurrentFacings(params: SpriteGenParams): Promise<void> {
  if (!params.persistTarget) return;
  const sprites = useIsoSpriteStore.getState().sprites;
  const facings: Partial<Record<SpriteFacing, string>> = {};
  for (const f of ALL_FACINGS) {
    const url = sprites[spriteKey(params.id, f)];
    if (url) facings[f] = url;
  }
  if (Object.keys(facings).length === 0) return;

  try {
    if (params.persistTarget.kind === 'character') {
      await trpc.sprites.saveToCharacter.mutate({
        characterId: params.persistTarget.characterId,
        sprites: facings,
      });
    } else if (params.persistTarget.kind === 'bestiary') {
      await trpc.sprites.saveToBestiary.mutate({
        bestiaryId: params.persistTarget.bestiaryId,
        sprites: facings,
      });
    } else {
      await trpc.sprites.saveToMob.mutate({
        mobId: params.persistTarget.mobId,
        sprites: facings,
      });
    }
  } catch (err) {
    console.warn(`[IsoSprites] Partial persist failed for ${params.name}:`, err);
  }
}

export interface SpriteGenParams {
  /** Stable UUID for this entity — cache key */
  id: string;
  /** Display name — used for the FLUX prompt, NOT the cache key */
  name: string;
  race?: string;
  className?: string;
  subclass?: string;
  equipment?: string[];
  personality?: string;
  appearance?: string;
  isNpc?: boolean;
  /**
   * DB persistence target. When provided, sprites are auto-uploaded to Storage
   * and URLs saved to the owning row as soon as all 4 facings finish — so other
   * players pick them up without the GM having to click Save manually.
   */
  persistTarget?:
    | { kind: 'character'; characterId: string }
    | { kind: 'bestiary'; bestiaryId: string }
    | { kind: 'mob'; mobId: string };
}

interface IsoSpriteState {
  /** All directional sprites: "id:facing" → base64 data URL */
  sprites: Record<string, string>;
  /** Current facing per entity id */
  facings: Record<string, SpriteFacing>;
  /** Seed images keyed by entity id */
  seeds: Record<string, string>;
  /** Display names keyed by entity id — for UI label recall */
  names: Record<string, string>;
  /** "id:facing" entries currently being generated */
  generating: Set<string>;
  generationErrors: Record<string, string>;

  // Actions — id-keyed
  loadFromCache: () => void;
  getSprite: (id: string, facing?: SpriteFacing) => string | undefined;
  getCurrentSprite: (id: string) => string | undefined;
  getFacing: (id: string) => SpriteFacing;
  setFacing: (id: string, facing: SpriteFacing) => void;
  setSeed: (id: string, imageUrl: string) => void;
  getSeed: (id: string) => string | undefined;
  promoteFacingToSeed: (id: string, facing: SpriteFacing) => void;

  generateSprite: (params: SpriteGenParams, facing: SpriteFacing) => Promise<string | null>;
  generateAllFacings: (params: SpriteGenParams) => Promise<void>;
  generateMissing: (entities: SpriteGenParams[]) => Promise<void>;

  facingCount: (id: string) => number;
  isGenerating: (id: string) => boolean;
  setCustomSprite: (id: string, imageUrl: string, facing: SpriteFacing, name?: string) => void;
  /** Apply the same image to all 4 facings for a given entity (e.g. GM upload). */
  setAllFacings: (id: string, imageUrl: string, name?: string) => void;
  /** Drop all sprites + seed for a given entity so the next generateAllFacings rebuilds fresh. */
  clearForId: (id: string) => void;
  clearAll: () => void;
}

export const useIsoSpriteStore = create<IsoSpriteState>((set, get) => ({
  sprites: {},
  facings: {},
  seeds: {},
  names: {},
  generating: new Set(),
  generationErrors: {},

  loadFromCache: () => {
    // Schema check — if we find legacy v1 (name-keyed) cache, drop it.
    const schema = localStorage.getItem(SCHEMA_VERSION_KEY);
    if (schema !== CURRENT_SCHEMA) {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(SEED_CACHE_KEY);
      localStorage.removeItem(NAME_CACHE_KEY);
      localStorage.setItem(SCHEMA_VERSION_KEY, CURRENT_SCHEMA);
      console.log('[IsoSprites] Upgraded cache to v2 (UUID-keyed) — legacy sprites cleared, regenerate at will');
      return;
    }

    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached) as Record<string, string>;
        // Filter to UUID-keyed entries only (drop any stray v1 strays)
        const filtered: Record<string, string> = {};
        for (const [key, val] of Object.entries(parsed)) {
          const [idPart] = key.split(':');
          if (idPart && UUID_LIKE.test(idPart)) {
            filtered[key] = val;
          }
        }
        const ids = new Set(Object.keys(filtered).map((k) => k.split(':')[0]));
        console.log(`[IsoSprites] Loaded ${Object.keys(filtered).length} sprites for ${ids.size} entities`);
        set({ sprites: filtered });
      }
    } catch (e) {
      console.warn('[IsoSprites] Failed to load sprite cache:', e);
    }

    try {
      const seedCached = localStorage.getItem(SEED_CACHE_KEY);
      if (seedCached) {
        const parsed = JSON.parse(seedCached) as Record<string, string>;
        const filtered: Record<string, string> = {};
        for (const [k, v] of Object.entries(parsed)) {
          if (UUID_LIKE.test(k)) filtered[k] = v;
        }
        set({ seeds: filtered });
      }
    } catch (e) {
      console.warn('[IsoSprites] Failed to load seed cache:', e);
    }

    try {
      const namesCached = localStorage.getItem(NAME_CACHE_KEY);
      if (namesCached) set({ names: JSON.parse(namesCached) });
    } catch (e) {
      console.warn('[IsoSprites] Failed to load names cache:', e);
    }
  },

  getSprite: (id, facing) => {
    const f = facing ?? get().facings[id] ?? 'south';
    const sprites = get().sprites;
    return sprites[spriteKey(id, f)] ?? sprites[spriteKey(id, 'south')];
  },

  getCurrentSprite: (id) => {
    const facing = get().facings[id] ?? 'south';
    const sprites = get().sprites;
    return sprites[spriteKey(id, facing)] ?? sprites[spriteKey(id, 'south')];
  },

  getFacing: (id) => get().facings[id] ?? 'south',

  setFacing: (id, facing) => {
    set((s) => ({ facings: { ...s.facings, [id]: facing } }));
  },

  setSeed: (id, imageUrl) => {
    set((s) => {
      const newSeeds = { ...s.seeds, [id]: imageUrl };
      try { localStorage.setItem(SEED_CACHE_KEY, JSON.stringify(newSeeds)); } catch { /* quota */ }
      return { seeds: newSeeds };
    });
  },

  getSeed: (id) => get().seeds[id],

  promoteFacingToSeed: (id, facing) => {
    const sprite = get().sprites[spriteKey(id, facing)];
    if (sprite) get().setSeed(id, sprite);
  },

  generateSprite: async (params, facing) => {
    const { id, name } = params;
    const key = spriteKey(id, facing);
    const state = get();

    if (state.sprites[key]) return state.sprites[key];
    if (state.generating.has(key)) return null;

    set((s) => {
      const newGen = new Set(s.generating);
      newGen.add(key);
      const newNames = s.names[id] !== name ? { ...s.names, [id]: name } : s.names;
      return { generating: newGen, names: newNames };
    });

    try {
      const seedImageUrl = get().seeds[id];
      const result = await trpc.maps.generateIsoSprite.mutate({
        name,
        race: params.race,
        className: params.className,
        subclass: params.subclass,
        equipment: params.equipment,
        personality: params.personality,
        appearance: params.appearance,
        isNpc: params.isNpc,
        facing,
        seedImageUrl,
      });

      set((s) => {
        const newSprites = { ...s.sprites, [key]: result.imageUrl };
        const newGen = new Set(s.generating);
        newGen.delete(key);
        const { [key]: _removed, ...errors } = s.generationErrors;

        try { localStorage.setItem(CACHE_KEY, JSON.stringify(newSprites)); } catch { /* quota */ }
        try { localStorage.setItem(NAME_CACHE_KEY, JSON.stringify(s.names)); } catch { /* quota */ }

        return { sprites: newSprites, generating: newGen, generationErrors: errors };
      });

      // Auto-promote south facing to seed on first gen
      if (facing === 'south' && !get().seeds[id]) {
        get().setSeed(id, result.imageUrl);
      }

      console.log(`[IsoSprites] Generated ${name} (id=${id.substring(0, 8)}…) facing ${facing}`);
      return result.imageUrl;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Generation failed';
      set((s) => {
        const newGen = new Set(s.generating);
        newGen.delete(key);
        return { generating: newGen, generationErrors: { ...s.generationErrors, [key]: msg } };
      });
      console.error(`[IsoSprites] Failed to generate ${name} facing ${facing}:`, msg);
      return null;
    }
  },

  generateAllFacings: async (params) => {
    const state = get();
    for (const facing of ACTIVE_FACINGS) {
      const key = spriteKey(params.id, facing);
      if (!state.sprites[key] && !state.generating.has(key)) {
        await get().generateSprite(params, facing);
        if (params.persistTarget) {
          await persistCurrentFacings(params);
        }
      }
    }
  },

  generateMissing: async (entities) => {
    const state = get();
    const needsWork = entities.filter((e) =>
      ACTIVE_FACINGS.some((f) => !state.sprites[spriteKey(e.id, f)] && !state.generating.has(spriteKey(e.id, f)))
    );
    if (needsWork.length === 0) return;

    const total = needsWork.reduce(
      (sum, e) => sum + ACTIVE_FACINGS.filter((f) => !state.sprites[spriteKey(e.id, f)]).length,
      0,
    );
    console.log(`[IsoSprites] Generating ${total} missing sprites across ${needsWork.length} entities`);

    for (const entity of needsWork) {
      await get().generateAllFacings(entity);
    }
  },

  facingCount: (id) => {
    const state = get();
    return ALL_FACINGS.filter((f) => !!state.sprites[spriteKey(id, f)]).length;
  },

  isGenerating: (id) => {
    const gen = get().generating;
    return ALL_FACINGS.some((f) => gen.has(spriteKey(id, f)));
  },

  setCustomSprite: (id, imageUrl, facing, name) => {
    set((s) => {
      const newSprites = { ...s.sprites, [spriteKey(id, facing)]: imageUrl };
      const newNames = name && s.names[id] !== name ? { ...s.names, [id]: name } : s.names;
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(newSprites)); } catch { /* quota */ }
      try { localStorage.setItem(NAME_CACHE_KEY, JSON.stringify(newNames)); } catch { /* quota */ }
      return { sprites: newSprites, names: newNames };
    });
  },

  setAllFacings: (id, imageUrl, name) => {
    set((s) => {
      const newSprites = { ...s.sprites };
      for (const f of ALL_FACINGS) newSprites[spriteKey(id, f)] = imageUrl;
      const newNames = name && s.names[id] !== name ? { ...s.names, [id]: name } : s.names;
      const newSeeds = { ...s.seeds, [id]: imageUrl };
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(newSprites)); } catch { /* quota */ }
      try { localStorage.setItem(NAME_CACHE_KEY, JSON.stringify(newNames)); } catch { /* quota */ }
      try { localStorage.setItem(SEED_CACHE_KEY, JSON.stringify(newSeeds)); } catch { /* quota */ }
      return { sprites: newSprites, names: newNames, seeds: newSeeds };
    });
  },

  clearForId: (id) => {
    set((s) => {
      const newSprites: Record<string, string> = {};
      for (const [k, v] of Object.entries(s.sprites)) {
        if (!k.startsWith(`${id}:`)) newSprites[k] = v;
      }
      const { [id]: _seedGone, ...seeds } = s.seeds;
      const { [id]: _nameGone, ...names } = s.names;
      const newGen = new Set<string>();
      for (const k of s.generating) {
        if (!k.startsWith(`${id}:`)) newGen.add(k);
      }
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(newSprites)); } catch { /* quota */ }
      try { localStorage.setItem(SEED_CACHE_KEY, JSON.stringify(seeds)); } catch { /* quota */ }
      try { localStorage.setItem(NAME_CACHE_KEY, JSON.stringify(names)); } catch { /* quota */ }
      return { sprites: newSprites, seeds, names, generating: newGen };
    });
  },

  clearAll: () => {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(SEED_CACHE_KEY);
    localStorage.removeItem(NAME_CACHE_KEY);
    set({
      sprites: {},
      facings: {},
      seeds: {},
      names: {},
      generating: new Set(),
      generationErrors: {},
    });
  },
}));
